import React, { useState } from "react";
import { CheckCircle, X, Download, ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight } from "lucide-react";

export default function MediaSection({ imageArray, selectedImages, setSelectedImages }) {
    const [previewImage, setPreviewImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });

    const toggleSelect = (index) => {
        if (selectedImages.includes(index)) {
            setSelectedImages(selectedImages.filter((i) => i !== index));
        } else {
            setSelectedImages([...selectedImages, index]);
        }
    };

    const openModal = (media, index) => {
        setPreviewImage(`http://image.onmls.com${media}`);
        setCurrentImageIndex(index);
        setZoom(1);
        setRotation(0);
        setImagePosition({ x: 0, y: 0 });
    };

    const closeModal = () => {
        setPreviewImage(null);
        setZoom(1);
        setRotation(0);
        setImagePosition({ x: 0, y: 0 });
    };

    const nextImage = () => {
        if (currentImageIndex < imageArray.length - 1) {
            const nextIndex = currentImageIndex + 1;
            setCurrentImageIndex(nextIndex);
            setPreviewImage(`http://image.onmls.com${imageArray[nextIndex].media}`);
            setZoom(1);
            setRotation(0);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const prevImage = () => {
        if (currentImageIndex > 0) {
            const prevIndex = currentImageIndex - 1;
            setCurrentImageIndex(prevIndex);
            setPreviewImage(`http://image.onmls.com${imageArray[prevIndex].media}`);
            setZoom(1);
            setRotation(0);
            setImagePosition({ x: 0, y: 0 });
        }
    };

    const zoomIn = () => {
        setZoom((prev) => Math.min(prev + 0.25, 3));
    };

    const zoomOut = () => {
        setZoom((prev) => Math.max(prev - 0.25, 0.5));
    };

    const rotateImage = () => {
        setRotation((prev) => (prev + 90) % 360);
    };

    const downloadImage = async () => {
        try {
            const response = await fetch(previewImage);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `image-${currentImageIndex + 1}.jpg`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading image:", error);
        }
    };

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - imagePosition.x,
                y: e.clientY - imagePosition.y,
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoom > 1) {
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y,
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleKeyDown = (e) => {
        if (previewImage) {
            switch (e.key) {
                case "Escape":
                    closeModal();
                    break;
                case "ArrowLeft":
                    prevImage();
                    break;
                case "ArrowRight":
                    nextImage();
                    break;
                case "+":
                case "=":
                    zoomIn();
                    break;
                case "-":
                    zoomOut();
                    break;
                case "r":
                case "R":
                    rotateImage();
                    break;
            }
        }
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            zoomIn();
        } else {
            zoomOut();
        }
    };

    React.useEffect(() => {
        if (previewImage) {
            document.addEventListener("keydown", handleKeyDown);
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.addEventListener("wheel", handleWheel, { passive: false });
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.removeEventListener("wheel", handleWheel);
            };
        }
    }, [previewImage, isDragging, dragStart, imagePosition, zoom]);

    return (
        <div className="tw-p-6 tw-h-full">
            <div className="tw-mb-4 tw-flex tw-flex-col tw-justify-between tw-items-start">
                <h2 className="tw-text-xl tw-font-semibold tw-text-gray-800">Images and Videos</h2>
                <p className="tw-text-sm tw-text-gray-600">(Dobuble click to view image)</p>
            </div>
            <div className="tw-flex tw-h-full tw-flex-wrap tw-gap-2">
                {imageArray && imageArray.length > 0 ? (
                    imageArray.map((item, index) => {
                        const isSelected = selectedImages.includes(index);
                        return (
                            <div
                                key={index}
                                onClick={() => toggleSelect(index)}
                                onDoubleClick={() => openModal(item.media, index)} // Double click to open modal
                                className={`tw-relative tw-w-[120px] tw-h-[120px] tw-border tw-rounded-xl tw-overflow-hidden tw-cursor-pointer tw-group ${
                                    isSelected ? "tw-ring-2 tw-ring-orange-500" : ""
                                }`}
                            >
                                {/* Image */}
                                <img src={`http://image.onmls.com${item.media}`} alt="Media" className="tw-w-full tw-h-full tw-object-cover" />

                                {/* Checkmark */}
                                {isSelected && (
                                    <div className="tw-absolute tw-top-2 tw-right-2 tw-bg-orange-500 tw-text-white tw-p-1 tw-rounded-full tw-z-10">
                                        <CheckCircle size={16} />
                                    </div>
                                )}

                                {/* Duration (optional placeholder) */}
                                <div className="tw-absolute tw-bottom-2 tw-left-2 tw-bg-black/60 tw-text-white tw-text-xs tw-px-2 tw-py-1 tw-rounded tw-flex tw-items-center tw-gap-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="tw-w-4 tw-h-4"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M15.75 9V5.25m0 0a.75.75 0 01.75-.75h1.5a.75.75 0 01.75.75V9m-3 0H6.75m0 0V5.25m0 0a.75.75 0 00-.75-.75H4.5a.75.75 0 00-.75.75V9m3 0h3m-3 0v6m9-6v6m3 0H6.75m0 0v3.75m0 0a.75.75 0 01-.75.75H4.5a.75.75 0 01-.75-.75V15m3 3.75h3"
                                        />
                                    </svg>
                                    1:23
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="tw-w-full tw-h-full tw-rounded-lg tw-flex tw-justify-center tw-items-center tw-bg-background_color">
                        Media Not Found
                    </div>
                )}
            </div>

            {/* Enhanced Image Modal */}
            {previewImage && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/95 tw-flex tw-items-center tw-justify-center tw-z-[9999] tw-backdrop-blur-sm">
                    {/* Modal Container */}
                    <div className="tw-relative tw-w-full tw-h-full tw-flex tw-flex-col">
                        {/* Header Bar */}
                        <div className="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-z-10 tw-bg-gradient-to-b tw-from-black/80 tw-to-transparent tw-p-4">
                            <div className="tw-flex tw-justify-between tw-items-center tw-text-white">
                                {/* Image Info */}
                                <div className="tw-flex tw-items-center tw-space-x-4">
                                    <h3 className="tw-text-lg tw-font-medium">
                                        Image {currentImageIndex + 1} of {imageArray.length}
                                    </h3>
                                    <div className="tw-text-sm tw-text-gray-300">Zoom: {Math.round(zoom * 100)}%</div>
                                </div>

                                {/* Action Buttons */}
                                <div className="tw-flex tw-items-center tw-space-x-2">
                                    <button
                                        onClick={downloadImage}
                                        className="tw-p-2 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-transition-all tw-duration-200 tw-backdrop-blur-sm"
                                        title="Download"
                                    >
                                        <Download size={20} />
                                    </button>
                                    <button
                                        onClick={zoomOut}
                                        disabled={zoom <= 0.5}
                                        className="tw-p-2 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-transition-all tw-duration-200 tw-backdrop-blur-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                        title="Zoom Out"
                                    >
                                        <ZoomOut size={20} />
                                    </button>
                                    <button
                                        onClick={zoomIn}
                                        disabled={zoom >= 3}
                                        className="tw-p-2 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-transition-all tw-duration-200 tw-backdrop-blur-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                        title="Zoom In"
                                    >
                                        <ZoomIn size={20} />
                                    </button>
                                    <button
                                        onClick={rotateImage}
                                        className="tw-p-2 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-transition-all tw-duration-200 tw-backdrop-blur-sm"
                                        title="Rotate"
                                    >
                                        <RotateCw size={20} />
                                    </button>
                                    <button
                                        onClick={closeModal}
                                        className="tw-p-2 tw-rounded-full tw-bg-red-500/20 hover:tw-bg-red-500/30 tw-transition-all tw-duration-200 tw-backdrop-blur-sm"
                                        title="Close (ESC)"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Navigation Arrows */}
                        {imageArray.length > 1 && (
                            <>
                                <button
                                    onClick={prevImage}
                                    disabled={currentImageIndex === 0}
                                    className="tw-absolute tw-left-4 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-z-10 tw-p-3 tw-rounded-full tw-bg-black/50 hover:tw-bg-black/70 tw-text-white tw-transition-all tw-duration-200 tw-backdrop-blur-sm disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                                    title="Previous Image (←)"
                                >
                                    <ChevronLeft size={24} />
                                </button>
                                <button
                                    onClick={nextImage}
                                    disabled={currentImageIndex === imageArray.length - 1}
                                    className="tw-absolute tw-right-4 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-z-10 tw-p-3 tw-rounded-full tw-bg-black/50 hover:tw-bg-black/70 tw-text-white tw-transition-all tw-duration-200 tw-backdrop-blur-sm disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
                                    title="Next Image (→)"
                                >
                                    <ChevronRight size={24} />
                                </button>
                            </>
                        )}

                        {/* Image Container */}
                        <div
                            className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-cursor-grab"
                            style={{ cursor: isDragging ? "grabbing" : zoom > 1 ? "grab" : "default" }}
                            onMouseDown={handleMouseDown}
                        >
                            <div className="tw-relative tw-max-w-full tw-max-h-full">
                                <img
                                    src={previewImage}
                                    alt="Preview"
                                    className="tw-max-w-none tw-max-h-none tw-transition-transform tw-duration-300 tw-ease-out tw-select-none"
                                    style={{
                                        transform: `scale(${zoom}) rotate(${rotation}deg) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                                        maxWidth: zoom === 1 ? "90vw" : "none",
                                        maxHeight: zoom === 1 ? "90vh" : "none",
                                        objectFit: "contain",
                                    }}
                                    draggable={false}
                                />
                            </div>
                        </div>

                        {/* Bottom Info Bar */}
                        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-bg-gradient-to-t tw-from-black/80 tw-to-transparent tw-p-4">
                            <div className="tw-text-center tw-text-white">
                                <div className="tw-text-sm tw-text-gray-300 tw-mb-2">
                                    Use arrow keys to navigate • Mouse wheel to zoom • Drag to pan when zoomed
                                </div>
                                {/* Image Thumbnails */}
                                {imageArray.length > 1 && (
                                    <div className="tw-flex tw-justify-center tw-space-x-2 tw-max-w-full tw-overflow-x-auto tw-pb-2">
                                        {imageArray.map((item, index) => (
                                            <button
                                                key={index}
                                                onClick={() => openModal(item.media, index)}
                                                className={`tw-relative tw-w-12 tw-h-12 tw-rounded tw-overflow-hidden tw-border-2 tw-transition-all tw-duration-200 tw-flex-shrink-0 ${
                                                    index === currentImageIndex
                                                        ? "tw-border-white tw-ring-2 tw-ring-white/50"
                                                        : "tw-border-white/30 hover:tw-border-white/60"
                                                }`}
                                            >
                                                <img
                                                    src={`http://image.onmls.com${item.media}`}
                                                    alt={`Thumbnail ${index + 1}`}
                                                    className="tw-w-full tw-h-full tw-object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Click outside to close */}
                    <div className="tw-absolute tw-inset-0 tw--z-10" onClick={closeModal} />
                </div>
            )}
        </div>
    );
}
